import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../config/api';
import type { ApiResponse, Job, JobFilters, PaginatedList } from '../../types';

interface JobsState {
  jobs: PaginatedList<Job> | null;
  currentJob: Job | null;
  loading: boolean;
  error: string | null;
}

const initialState: JobsState = {
  jobs: null,
  currentJob: null,
  loading: false,
  error: null,
};

// ─── Async Thunks ─────────────────────────────────────────────────────────

export const fetchJobs = createAsyncThunk<
  PaginatedList<Job>,
  JobFilters | undefined,
  { rejectValue: string }
>('jobs/fetchJobs', async (filters = {}, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    if (filters.keyword) params.append('keyword', filters.keyword);
    if (filters.location) params.append('location', filters.location);
    if (filters.jobType) params.append('jobType', filters.jobType);
    if (filters.experienceLevel) params.append('experienceLevel', filters.experienceLevel);
    if (filters.salaryMin !== undefined) params.append('salaryMin', String(filters.salaryMin));
    if (filters.salaryMax !== undefined) params.append('salaryMax', String(filters.salaryMax));
    if (filters.status) params.append('status', filters.status);
    if (filters.companyId) params.append('companyId', filters.companyId);
    params.append('pageNumber', String(filters.pageNumber ?? 1));
    params.append('pageSize', String(filters.pageSize ?? 20));
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDescending !== undefined)
      params.append('sortDescending', String(filters.sortDescending));
    if (filters.minExperienceYears !== undefined)
      params.append('minExperienceYears', String(filters.minExperienceYears));
    if (filters.maxExperienceYears !== undefined)
      params.append('maxExperienceYears', String(filters.maxExperienceYears));
    if (filters.isImported !== undefined)
      params.append('isImported', String(filters.isImported));
    if (filters.importSource) params.append('importSource', filters.importSource);

    const response = await api.get<ApiResponse<PaginatedList<Job>>>(`/jobs?${params.toString()}`);
    if (!response.data.success || !response.data.data) {
      return rejectWithValue(response.data.message || 'Failed to fetch jobs');
    }
    return response.data.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch jobs');
  }
});

export const fetchJobById = createAsyncThunk<Job, string, { rejectValue: string }>(
  'jobs/fetchJobById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<Job>>(`/jobs/${id}`);
      if (!response.data.success || !response.data.data) {
        return rejectWithValue(response.data.message || 'Job not found');
      }
      return response.data.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Job not found');
    }
  }
);

export const createJob = createAsyncThunk<Job, Partial<Job>, { rejectValue: string }>(
  'jobs/createJob',
  async (jobData, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<Job>>('/jobs', jobData);
      if (!response.data.success || !response.data.data) {
        return rejectWithValue(response.data.message || 'Failed to create job');
      }
      return response.data.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to create job');
    }
  }
);

export const updateJob = createAsyncThunk<
  Job,
  { id: string; data: Partial<Job> },
  { rejectValue: string }
>('jobs/updateJob', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.put<ApiResponse<Job>>(`/jobs/${id}`, data);
    if (!response.data.success || !response.data.data) {
      return rejectWithValue(response.data.message || 'Failed to update job');
    }
    return response.data.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    return rejectWithValue(err.response?.data?.message || 'Failed to update job');
  }
});

export const deleteJob = createAsyncThunk<string, string, { rejectValue: string }>(
  'jobs/deleteJob',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete<ApiResponse>(`/jobs/${id}`);
      if (!response.data.success) {
        return rejectWithValue(response.data.message || 'Failed to delete job');
      }
      return id;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(err.response?.data?.message || 'Failed to delete job');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    clearCurrentJob(state) {
      state.currentJob = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchJobs
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = action.payload;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error fetching jobs';
      });

    // fetchJobById
    builder
      .addCase(fetchJobById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentJob = action.payload;
      })
      .addCase(fetchJobById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error fetching job';
      });

    // createJob
    builder
      .addCase(createJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.loading = false;
        if (state.jobs) {
          state.jobs.items = [action.payload, ...state.jobs.items];
          state.jobs.totalCount += 1;
        }
      })
      .addCase(createJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error creating job';
      });

    // updateJob
    builder
      .addCase(updateJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.loading = false;
        state.currentJob = action.payload;
        if (state.jobs) {
          state.jobs.items = state.jobs.items.map((j) =>
            j.id === action.payload.id ? action.payload : j
          );
        }
      })
      .addCase(updateJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error updating job';
      });

    // deleteJob
    builder
      .addCase(deleteJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.loading = false;
        if (state.jobs) {
          state.jobs.items = state.jobs.items.filter((j) => j.id !== action.payload);
          state.jobs.totalCount -= 1;
        }
        if (state.currentJob?.id === action.payload) {
          state.currentJob = null;
        }
      })
      .addCase(deleteJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error deleting job';
      });
  },
});

export const { clearCurrentJob, clearError } = jobsSlice.actions;
export default jobsSlice.reducer;
